import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";

import {
	$commitCellDraft,
	$compilationEffect,
	$compiledCells,
	$flushCompilation,
	$renameComputeCell,
	$rerunComputeCell,
} from "../../internal/runtime";

/**
 * Hook to access compiled cells for a page.
 *
 * This hook:
 * - Returns the current compiled cells
 * - Automatically subscribes to the compilation effect (starts watching editor state)
 * - Provides a flush function to trigger immediate compilation
 * - Provides a commitCellDraft function to update compute cell content
 * - Provides a rerunComputeCell function to rerun a cell (replaces with new ID to trigger re-run)
 * - Provides a renameComputeCell function to rename a cell
 *
 * The return type is `unknown` - cast it to your CellMap type in the consuming code.
 *
 * @param documentId - The document ID
 * @param pageId - The page ID
 */
export const useCompiledCells = (documentId: string, pageId: string) => {
	// Subscribe to the compilation effect - this starts the watcher
	useAtomValue($compilationEffect({ documentId, pageId }));

	// Get the compiled cells
	const compiledCells = useAtomValue($compiledCells({ documentId, pageId }));

	// Get the flush setter
	const setFlush = useSetAtom($flushCompilation({ documentId, pageId }));

	// Get the commit cell draft setter
	const commitCellDraft = useSetAtom($commitCellDraft({ documentId, pageId }));

	// Get the rerun compute cell setter
	const rerunComputeCell = useSetAtom($rerunComputeCell({ documentId, pageId }));

	// Get the rename compute cell setter
	const renameComputeCell = useSetAtom($renameComputeCell({ documentId, pageId }));

	// Flush function triggers immediate compilation
	const flushCompilation = useCallback(() => {
		setFlush(true);
	}, [setFlush]);

	return {
		/** The current compiled cells (type as CellMap in consuming code) */
		compiledCells,
		/** Trigger immediate compilation, skipping the debounce */
		flushCompilation,
		/** Commit a cell's draft code to the document and trigger compilation */
		commitCellDraft,
		/** Rerun a compute cell (replaces with new ID to trigger re-run) */
		rerunComputeCell,
		/** Rename a compute cell (returns { error } if invalid, undefined on success) */
		renameComputeCell,
	};
};

/**
 * Hook to trigger immediate compilation of cells.
 *
 * This is a lighter-weight alternative to useCompiledCells when you only
 * need the flush capability without subscribing to the compiled cells.
 *
 * @param documentId - The document ID
 * @param pageId - The page ID
 */
export const useFlushCompilation = (documentId: string, pageId: string) => {
	const setFlush = useSetAtom($flushCompilation({ documentId, pageId }));

	return useCallback(() => {
		setFlush(true);
	}, [setFlush]);
};
